import {useCallback, useMemo} from 'react';
import {produce} from 'immer';
import type {
  FilterModel,
  PaginationModel,
  Sorting,
} from 'services/settings/types';
import {
  selectSettingsTables,
  tableColumnConfigUpdated,
  tableFilterModelUpdated,
  tablePaginationModelResetted,
  tablePaginationModelUpdated,
  tableSortingUpdated,
} from 'services/settings';
import {useAppDispatch, useAppSelector} from 'store/hooks';
import type {ColumnConfig} from '../types';

export type ColumnConfigStateProps = {
  columnConfigs: ColumnConfig[] | undefined;
  setColumnConfigs: (columnConfigs: ColumnConfig[]) => void;
  setColumnConfigVisibility: (columnName: string, visible: boolean) => void;
  setColumnConfigsVisibilities: (
    columnNames: string[],
    visible?: boolean,
  ) => void;
  setColumnConfigWidth: (columnName: string, width: number) => void;
  sortingConfig?: Sorting;
  setSortingConfig: (sorting?: Sorting) => void;
  filterModel?: FilterModel | undefined;
  setFilterModel: (filterModel?: FilterModel) => void;
  paginationModel: PaginationModel;
  setPaginationModel: (paginationModel: PaginationModel) => void;
  resetPaginationModel: () => void;
};

export const useColumnConfigState = (
  schemaName: string,
): ColumnConfigStateProps => {
  const dispatch = useAppDispatch();
  const tablesState = useAppSelector(selectSettingsTables);

  const columnConfigs = useMemo(() => {
    return tablesState[schemaName]?.columns;
  }, [tablesState, schemaName]);

  const sortingConfig = useMemo(() => {
    return tablesState[schemaName]?.sorting ?? undefined;
  }, [tablesState, schemaName]);

  const setSortingConfig = useCallback(
    (sorting?: Sorting) => {
      dispatch(
        tableSortingUpdated({
          name: schemaName,
          sorting: sorting,
        }),
      );
    },
    [schemaName, dispatch],
  );

  const filterModel = useMemo(() => {
    return tablesState[schemaName]?.filterModel ?? undefined;
  }, [tablesState, schemaName]);

  const setFilterModel = useCallback(
    (filterModel?: FilterModel) => {
      dispatch(
        tableFilterModelUpdated({
          name: schemaName,
          filterModel: filterModel,
        }),
      );
    },
    [schemaName, dispatch],
  );

  const paginationModel = useMemo(() => {
    return (
      tablesState[schemaName]?.paginationModel ?? {
        page: 0,
        pageSize: 20,
      }
    );
  }, [tablesState, schemaName]);

  const setPaginationModel = useCallback(
    (paginationModel: PaginationModel) => {
      dispatch(
        tablePaginationModelUpdated({
          name: schemaName,
          paginationModel: paginationModel,
        }),
      );
    },
    [schemaName, dispatch],
  );

  const resetPaginationModel = useCallback(() => {
    dispatch(
      tablePaginationModelResetted({
        name: schemaName,
        pageSize: paginationModel.pageSize,
      }),
    );
  }, [dispatch, paginationModel.pageSize, schemaName]);

  const setColumnConfigs = useCallback(
    (columnConfigs: ColumnConfig[]) => {
      dispatch(
        tableColumnConfigUpdated({
          name: schemaName,
          config: columnConfigs,
        }),
      );
    },
    [schemaName, dispatch],
  );

  const setColumnConfigVisibility = useCallback(
    (columnName: string, visible: boolean) => {
      const newColumnConfigState = produce(columnConfigs, draft => {
        const columnConfig = draft.find(s => s.name === columnName);
        if (columnConfig) {
          columnConfig.visible = visible;
        } else {
          draft.push({
            name: columnName,
            visible: visible,
          });
        }
      });

      setColumnConfigs(newColumnConfigState);
    },
    [columnConfigs, setColumnConfigs],
  );

  const setColumnConfigsVisibilities = useCallback(
    (columnNames: string[], visible: boolean = true) => {
      const newColumnConfigs = produce(columnConfigs, draft => {
        // Set all visibles to oposite value (e.q. all to invisible)
        draft.forEach(columnConfig => {
          columnConfig.visible = !visible;
        });

        // Update all columnNames to provided visiblity (e.g. visible)
        columnNames.forEach(columnName => {
          const columnConfig = draft.find(c => c.name === columnName);
          if (columnConfig) {
            columnConfig.visible = visible;
          } else {
            draft.push({
              name: columnName,
              visible: visible,
            });
          }
        });
      });

      setColumnConfigs(newColumnConfigs);
    },
    [columnConfigs, setColumnConfigs],
  );

  const setColumnConfigWidth = useCallback(
    (columnName: string, width: number) => {
      const newColumnConfigState = produce(columnConfigs, draft => {
        const columnConfig = draft.find(s => s.name === columnName);
        if (columnConfig) {
          columnConfig.width = width;
        } else {
          columnConfigs.push({
            name: columnName,
            width: width,
            visible: true, // Default true when handled
          });
        }
      });

      setColumnConfigs(newColumnConfigState);
    },
    [columnConfigs, setColumnConfigs],
  );

  return {
    columnConfigs,
    setColumnConfigs,
    setColumnConfigVisibility,
    setColumnConfigsVisibilities,
    setColumnConfigWidth,
    sortingConfig,
    setSortingConfig,
    filterModel,
    setFilterModel,
    paginationModel,
    setPaginationModel,
    resetPaginationModel,
  };
};
